/**
 * StickyFilter Manager
 * 
 * Implements dynamic sticky transformation for filter blocks:
 * - Detects header height dynamically
 * - Uses IntersectionObserver for performance
 * - Adds .is-sticky class when filters reach header bottom
 * - Prevents layout shift by maintaining consistent height
 */
export default class StickyFilter {
  constructor() {
    this.header = null;
    this.filterElements = [];
    this.observers = [];
    this.resizeObserver = null;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Find the sticky header (second navbar with sticky-top class)
    this.header = document.querySelector('nav.navbar.sticky-top');
    
    if (!this.header) {
      console.warn('[StickyFilter] Header not found');
      return;
    }

    // Find all filter components
    const filterComponents = document.querySelectorAll('.component.filter');
    
    if (filterComponents.length === 0) {
      return;
    }

    // Initialize header height and set CSS variable
    this.updateHeaderOffset();

    // Setup resize observer for header height changes
    this.setupResizeObserver();

    // Setup intersection observers for each filter
    filterComponents.forEach((filterComponent) => {
      const filtersElement = filterComponent.querySelector('.filters');
      
      if (filtersElement) {
        this.setupFilterObserver(filterComponent, filtersElement);
        this.setupMobileScroll(filtersElement);
      }
    });
  }

  /**
   * Update CSS variable with current header height
   */
  updateHeaderOffset() {
    if (this.header) {
      const headerHeight = this.header.offsetHeight;
      document.documentElement.style.setProperty('--header-offset', `${headerHeight}px`);
    }
  }

  /**
   * Setup ResizeObserver to update header offset on resize
   */
  setupResizeObserver() {
    if (!window.ResizeObserver) {
      // Fallback: use window resize event
      let resizeTimeout;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          this.updateHeaderOffset();
        }, 150);
      });
      return;
    }

    this.resizeObserver = new ResizeObserver((entries) => {
      this.updateHeaderOffset();
    });

    this.resizeObserver.observe(this.header);
  }

  /**
   * Setup IntersectionObserver for a single filter element
   * @param {HTMLElement} filterComponent - The .component.filter element
   * @param {HTMLElement} filtersElement - The .filters element inside
   */
  setupFilterObserver(filterComponent, filtersElement) {
    // Store initial height to prevent layout shift
    const initialHeight = filtersElement.offsetHeight;
    filtersElement.style.minHeight = `${initialHeight}px`;

    // Calculate header bottom position
    const getHeaderBottom = () => {
      if (!this.header) return 0;
      const headerRect = this.header.getBoundingClientRect();
      return headerRect.bottom;
    };

    // Check if filter should be sticky
    const checkStickyState = () => {
      const headerBottom = getHeaderBottom();
      const filterRect = filterComponent.getBoundingClientRect();
      
      // When filter top crosses or is below header bottom, activate sticky
      if (filterRect.top <= headerBottom) {
        if (!filtersElement.classList.contains('is-sticky')) {
          filtersElement.classList.add('is-sticky');
        }
      } else {
        if (filtersElement.classList.contains('is-sticky')) {
          filtersElement.classList.remove('is-sticky');
        }
      }
    };

    // Create a sentinel element positioned at the top of filterComponent
    // This will be used to detect when the filter reaches the header
    const sentinel = document.createElement('div');
    sentinel.style.cssText = 'position: absolute; top: 0; left: 0; width: 1px; height: 1px; pointer-events: none; visibility: hidden; z-index: -1;';
    
    // Make filterComponent relative positioned if not already
    const computedPosition = window.getComputedStyle(filterComponent).position;
    if (computedPosition === 'static') {
      filterComponent.style.position = 'relative';
    }
    
    filterComponent.insertBefore(sentinel, filterComponent.firstChild);

    // Setup IntersectionObserver on sentinel
    // We'll check position manually in the callback for accuracy
    const observer = new IntersectionObserver(
      (entries) => {
        checkStickyState();
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: [0, 1]
      }
    );

    observer.observe(sentinel);
    this.observers.push({ observer, sentinel, filterComponent, filtersElement });

    // Throttled scroll handler for immediate feedback
    let scrollTimeout;
    const handleScroll = () => {
      if (scrollTimeout) return;
      scrollTimeout = requestAnimationFrame(() => {
        checkStickyState();
        scrollTimeout = null;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    this.observers.push({ scrollHandler: handleScroll });

    // Initial check
    checkStickyState();

    // Update min-height on resize to prevent layout shift
    const updateMinHeight = () => {
      const currentHeight = filtersElement.offsetHeight;
      const currentMinHeight = parseFloat(filtersElement.style.minHeight) || 0;
      // Always use the larger height to prevent shrinking
      if (currentHeight > currentMinHeight) {
        filtersElement.style.minHeight = `${currentHeight}px`;
      }
    };

    // Observe filters element for size changes
    if (window.ResizeObserver) {
      const filtersResizeObserver = new ResizeObserver(() => {
        updateMinHeight();
      });
      filtersResizeObserver.observe(filtersElement);
      this.observers.push({ resizeObserver: filtersResizeObserver });
    } else {
      // Fallback: update on window resize
      let resizeTimeout;
      const handleResize = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          updateMinHeight();
          checkStickyState();
        }, 150);
      };
      window.addEventListener('resize', handleResize);
      this.observers.push({ resizeHandler: handleResize });
    }
  }

  /**
   * Setup mobile scroll enhancement
   * Shows scrollbar during scroll for better UX
   * @param {HTMLElement} filtersElement - The .filters element
   */
  setupMobileScroll(filtersElement) {
    // Only on mobile devices
    if (window.innerWidth > 768) {
      return;
    }

    let scrollTimeout;
    let isScrolling = false;

    const handleScroll = () => {
      if (!isScrolling) {
        isScrolling = true;
        filtersElement.classList.add('scrolling');
      }

      // Clear existing timeout
      clearTimeout(scrollTimeout);

      // Hide scrollbar after scrolling stops
      scrollTimeout = setTimeout(() => {
        isScrolling = false;
        filtersElement.classList.remove('scrolling');
      }, 1000);
    };

    // Listen to scroll events
    filtersElement.addEventListener('scroll', handleScroll, { passive: true });
    filtersElement.addEventListener('touchstart', () => {
      filtersElement.classList.add('scrolling');
    }, { passive: true });
    filtersElement.addEventListener('touchend', () => {
      setTimeout(() => {
        filtersElement.classList.remove('scrolling');
      }, 1000);
    }, { passive: true });

    // Store for cleanup
    this.observers.push({
      scrollHandler: handleScroll,
      filtersElement: filtersElement
    });
  }

  /**
   * Cleanup observers (useful for SPA or dynamic content)
   */
  destroy() {
    this.observers.forEach(({ observer, resizeObserver, scrollHandler, resizeHandler }) => {
      if (observer) {
        observer.disconnect();
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (scrollHandler) {
        window.removeEventListener('scroll', scrollHandler);
      }
      if (resizeHandler) {
        window.removeEventListener('resize', resizeHandler);
      }
    });
    
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    
    this.observers = [];
  }
}
